import { useEffect, useState } from 'react'
import { enregistrerReglages, testerCanal, type ReglagesAlertes } from '../../api/alertes'
import { useDroits } from '../Contexte'
import { Icone } from '../Icone'
import { ecrire } from './AlertesOutils'

/**
 * Onglet « Notifications » : où partent l'e-mail et Telegram. L'historique n'a rien à régler.
 *
 * - E-mail : une adresse par ligne (5 au plus) ; vide = les propriétaires et dispatchers de l'organisation.
 * - Telegram : l'identifiant de VOTRE conversation (le bot est celui de la plateforme). Il n'est utilisé qu'après un message d'essai
 *   réussi : on ne laisse pas un identifiant tapé au hasard recevoir des alertes.
 */
export function OngletNotifications({
  reglages,
  orgId,
  gere,
  surNotifier,
  surChangement,
}: {
  reglages: ReglagesAlertes
  orgId: string
  gere: boolean
  surNotifier: (message: string) => void
  surChangement: () => Promise<void>
}) {
  const { apercu } = useDroits()
  const [emails, setEmails] = useState(reglages.emails.join('\n'))
  const [chat, setChat] = useState(reglages.telegramChatId ?? '')
  const [essai, setEssai] = useState<'email' | 'telegram' | null>(null)
  const [retour, setRetour] = useState<{ ok: boolean; texte: string } | null>(null)

  useEffect(() => {
    setEmails(reglages.emails.join('\n'))
    setChat(reglages.telegramChatId ?? '')
  }, [reglages])

  const liste = emails
    .split(/[\n,;]+/)
    .map((x) => x.trim())
    .filter(Boolean)
  const modifie = liste.join('|') !== reglages.emails.join('|') || chat.trim() !== (reglages.telegramChatId ?? '')
  const chatValide = chat.trim() === '' || /^-?\d{1,20}$/.test(chat.trim())

  async function enregistrer() {
    if (!chatValide) {
      surNotifier('L’identifiant Telegram est un nombre (négatif pour un groupe).')
      return
    }
    if (liste.length > 5) {
      surNotifier('Cinq adresses e-mail au plus.')
      return
    }
    const ok = await ecrire({ apercu, surNotifier }, () => enregistrerReglages(orgId, { emails: liste, telegramChatId: chat.trim() || null }), 'Notifications enregistrées')
    if (ok) await surChangement()
  }

  async function tester(canal: 'email' | 'telegram') {
    if (apercu) {
      surNotifier('Aperçu : aucun message n’est envoyé.')
      return
    }
    setEssai(canal)
    setRetour(null)
    try {
      setRetour({ ok: true, texte: await testerCanal(orgId, canal) })
      await surChangement()
    } catch (e) {
      setRetour({ ok: false, texte: e instanceof Error ? e.message : String(e) })
    } finally {
      setEssai(null)
    }
  }

  return (
    <div className="card" data-anim>
      <div className="card-head">
        <div>
          <h3>
            <Icone nom="send" classe="h-ic" /> Notifications
          </h3>
          <div className="ch-sub">
            L’historique est toujours alimenté. L’e-mail et Telegram se choisissent règle par règle (onglet Règles) ; ici, vous dites où ils partent.
          </div>
        </div>
      </div>
      <div className="card-body form">
        <div className="form-grid">
          <div className="field form-full">
            <label htmlFor="notif-emails">Adresses e-mail (une par ligne)</label>
            <textarea
              id="notif-emails"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              disabled={!gere}
              rows={3}
              placeholder="alertes@votre-societe.ml"
            />
            <div className="t-muted">Vide : les propriétaires et dispatchers de l’organisation reçoivent les e-mails. Cinq adresses au plus.</div>
          </div>
          <div className="field">
            <label htmlFor="notif-chat">Identifiant de la conversation Telegram</label>
            <input
              id="notif-chat"
              value={chat}
              onChange={(e) => setChat(e.target.value)}
              disabled={!gere}
              inputMode="numeric"
              placeholder="-1001234567890"
              aria-invalid={!chatValide}
            />
            <div className="t-muted">
              Un nombre, négatif pour un groupe (ex. -1001234567890).{' '}
              {reglages.telegramChatId ? (reglages.telegramVerifie ? 'Conversation vérifiée.' : 'Pas encore vérifiée : envoyez un message d’essai.') : ''}
            </div>
          </div>
          {gere && (
            <div className="field form-full">
              <div className="actions-ligne">
                <button className="btn btn-gold btn-sm" onClick={() => void enregistrer()} disabled={!modifie || !chatValide}>
                  Enregistrer
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => void tester('email')} disabled={modifie || essai !== null}>
                  {essai === 'email' ? 'Envoi…' : 'Essai e-mail'}
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => void tester('telegram')} disabled={modifie || essai !== null || !reglages.telegramChatId}>
                  {essai === 'telegram' ? 'Envoi…' : 'Essai Telegram'}
                </button>
              </div>
            </div>
          )}
        </div>
        {retour && (
          <div className={`bandeau${retour.ok ? '' : ' erreur'}`} role="status" style={{ marginTop: 12, marginBottom: 0 }}>
            {retour.texte}
          </div>
        )}
        <details style={{ marginTop: 16 }}>
          <summary className="t-strong">Comment brancher Telegram ?</summary>
          <ol className="t-muted" style={{ margin: '8px 0 0 18px', display: 'grid', gap: 4 }}>
            <li>Créez une conversation (ou un groupe) Telegram et ajoutez-y le bot d’alertes SwiftDeliver.</li>
            <li>
              Écrivez un message dans la conversation, puis ajoutez-y temporairement un bot comme @RawDataBot : il affiche l’identifiant de la conversation.
              Retirez-le ensuite.
            </li>
            <li>Collez l’identifiant ci-dessus, enregistrez, puis « Essai Telegram ». Le message d’essai doit arriver : la conversation est alors vérifiée.</li>
          </ol>
        </details>
        <p className="t-muted" style={{ marginTop: 12 }}>
          Délai d’envoi : une minute au plus après le déclenchement. Une alerte n’est notifiée qu’une fois, à sa création. Si trop d’alertes arrivent d’un coup,
          elles sont regroupées dans un seul message.
        </p>
      </div>
    </div>
  )
}
